// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the desktop sidebar's page buttons (not the DM and community rows),
// named with the phone rail's ids. GuildsLayout decides visibility and order
// from here, Settings > Appearance > Sidebar edits the order and the hidden
// set, the avatar pop-up lists the hidden ones under "More", and Settings >
// "Open Konvo on" offers the visible ones. Home is always first and always
// shown. Spaces are not pages: they keep their own place under Chats.

import type {MobileRailTabId} from '@app/features/app/components/layout/mobile_rail/MobileRailTypes';

/** Every page button in its default order. New pages slot in after their default neighbour. */
export const SIDEBAR_PAGE_IDS = [
	'home',
	'chats',
	'calendar',
	'mail',
	'agents',
	'clickup',
	'whatsapp',
	'scheduled',
] as const satisfies ReadonlyArray<MobileRailTabId>;

export type SidebarPageId = (typeof SIDEBAR_PAGE_IDS)[number];

/** The account-synced choice: every page id in sidebar order, plus the ones the user switched off. */
export interface DesktopSidebarPrefs {
	order: Array<SidebarPageId>;
	hidden: Array<SidebarPageId>;
}

export const DEFAULT_DESKTOP_SIDEBAR: Readonly<DesktopSidebarPrefs> = Object.freeze({
	order: [...SIDEBAR_PAGE_IDS],
	hidden: [],
});

export interface SidebarPageConditions {
	/** Instance config: DMs off hides the Chats button. */
	directMessagesDisabled: boolean;
	/** WhatsApp is opt-in; its button only exists once the account is linked. */
	whatsappLinked: boolean;
	/** Coding agents are switched on per person. */
	agentsEnabled: boolean;
}

export function isSidebarPageId(value: unknown): value is SidebarPageId {
	return typeof value === 'string' && (SIDEBAR_PAGE_IDS as ReadonlyArray<string>).includes(value);
}

/**
 * Known ids only, no duplicates, Home first, and every page present: a page the
 * saved order does not know yet (Agents, for orders saved before it existed) goes
 * straight after the nearest page that precedes it by default. Home is never hidden.
 */
export function sanitizeDesktopSidebar(raw: unknown): DesktopSidebarPrefs {
	const source = raw != null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
	const rawOrder = Array.isArray(source['order']) ? source['order'] : [];
	const rawHidden = Array.isArray(source['hidden']) ? source['hidden'] : [];
	const order: Array<SidebarPageId> = ['home'];
	for (const id of rawOrder) {
		if (!isSidebarPageId(id) || order.includes(id)) continue;
		order.push(id);
	}
	for (let index = 1; index < SIDEBAR_PAGE_IDS.length; index++) {
		const id = SIDEBAR_PAGE_IDS[index];
		if (order.includes(id)) continue;
		let insertAt = 1;
		for (let previous = index - 1; previous >= 0; previous--) {
			const position = order.indexOf(SIDEBAR_PAGE_IDS[previous]);
			if (position >= 0) {
				insertAt = position + 1;
				break;
			}
		}
		order.splice(insertAt, 0, id);
	}
	const hidden: Array<SidebarPageId> = [];
	for (const id of rawHidden) {
		if (!isSidebarPageId(id) || id === 'home' || hidden.includes(id)) continue;
		hidden.push(id);
	}
	return {order, hidden};
}

/** Whether the page exists for this person at all (before their own show/hide choice). */
export function isSidebarPageAvailable(id: SidebarPageId, conditions: SidebarPageConditions): boolean {
	switch (id) {
		case 'chats':
			return !conditions.directMessagesDisabled;
		case 'whatsapp':
			return conditions.whatsappLinked;
		case 'agents':
			return conditions.agentsEnabled;
		case 'home':
		case 'calendar':
		case 'mail':
		case 'clickup':
		case 'scheduled':
			return true;
	}
}

/** Whether the page has a button in the sidebar: available and not switched off. */
export function isSidebarPageVisible(
	id: SidebarPageId,
	conditions: SidebarPageConditions,
	prefs: Readonly<DesktopSidebarPrefs> = DEFAULT_DESKTOP_SIDEBAR,
): boolean {
	if (!isSidebarPageAvailable(id, conditions)) return false;
	return id === 'home' || !prefs.hidden.includes(id);
}

/** Pages that exist for this person, in their sidebar order (the Settings list). */
export function availableSidebarPages(
	conditions: SidebarPageConditions,
	prefs: Readonly<DesktopSidebarPrefs> = DEFAULT_DESKTOP_SIDEBAR,
): Array<SidebarPageId> {
	return prefs.order.filter((id) => isSidebarPageAvailable(id, conditions));
}

/** Pages with a sidebar button, in sidebar order. */
export function visibleSidebarPages(
	conditions: SidebarPageConditions,
	prefs: Readonly<DesktopSidebarPrefs> = DEFAULT_DESKTOP_SIDEBAR,
): Array<SidebarPageId> {
	return prefs.order.filter((id) => isSidebarPageVisible(id, conditions, prefs));
}

/** Pages the user switched off, in sidebar order: the avatar pop-up's "More" tiles. */
export function hiddenSidebarPages(
	conditions: SidebarPageConditions,
	prefs: Readonly<DesktopSidebarPrefs>,
): Array<SidebarPageId> {
	return prefs.order.filter(
		(id) => isSidebarPageAvailable(id, conditions) && !isSidebarPageVisible(id, conditions, prefs),
	);
}

export interface SidebarPageLayout {
	/** Page buttons between Home's divider and Chats. */
	beforeChats: Array<SidebarPageId>;
	/** Page buttons after the DM and space rows. */
	afterChats: Array<SidebarPageId>;
}

/**
 * Where the visible page buttons sit around the Chats block. Home always leads.
 * Chats keeps its unread DMs and the spaces directly under it, so pages ordered
 * before Chats go above that block and the rest below it. Chats' slot holds even
 * while Chats itself is hidden, so hiding it never moves the spaces.
 */
export function sidebarPageLayout(
	conditions: SidebarPageConditions,
	prefs: Readonly<DesktopSidebarPrefs> = DEFAULT_DESKTOP_SIDEBAR,
): SidebarPageLayout {
	const chatsIndex = prefs.order.indexOf('chats');
	const beforeChats: Array<SidebarPageId> = [];
	const afterChats: Array<SidebarPageId> = [];
	prefs.order.forEach((id, index) => {
		if (id === 'home' || id === 'chats' || !isSidebarPageVisible(id, conditions, prefs)) return;
		if (chatsIndex >= 0 && index < chatsIndex) beforeChats.push(id);
		else afterChats.push(id);
	});
	return {beforeChats, afterChats};
}

/** Switch a page on or off. Home cannot be switched off. */
export function setSidebarPageHidden(
	prefs: Readonly<DesktopSidebarPrefs>,
	id: SidebarPageId,
	hidden: boolean,
): DesktopSidebarPrefs {
	if (id === 'home') return sanitizeDesktopSidebar(prefs);
	const rest = prefs.hidden.filter((other) => other !== id);
	return sanitizeDesktopSidebar({order: prefs.order, hidden: hidden ? [...rest, id] : rest});
}

/**
 * Move `id` to `toIndex` within `shown` (the list the user sees, which can leave out
 * pages that are unavailable to them). Pages not in `shown` keep their slots in the
 * full order. Home stays first: nothing moves above it and it does not move.
 */
export function moveSidebarPage(
	prefs: Readonly<DesktopSidebarPrefs>,
	shown: ReadonlyArray<SidebarPageId>,
	id: SidebarPageId,
	toIndex: number,
): DesktopSidebarPrefs {
	const from = shown.indexOf(id);
	if (id === 'home' || from < 0) return sanitizeDesktopSidebar(prefs);
	const minIndex = shown[0] === 'home' ? 1 : 0;
	const target = Math.max(minIndex, Math.min(shown.length - 1, toIndex));
	if (target === from) return sanitizeDesktopSidebar(prefs);
	const arranged = [...shown];
	arranged.splice(from, 1);
	arranged.splice(target, 0, id);
	const shownSet = new Set(shown);
	let next = 0;
	const order = prefs.order.map((pageId) => (shownSet.has(pageId) ? arranged[next++] : pageId));
	return sanitizeDesktopSidebar({order, hidden: prefs.hidden});
}

export function sameDesktopSidebar(a: Readonly<DesktopSidebarPrefs>, b: Readonly<DesktopSidebarPrefs>): boolean {
	return a.order.join(',') === b.order.join(',') && [...a.hidden].sort().join(',') === [...b.hidden].sort().join(',');
}
